import { useEffect, useState } from "react";
import { Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const LAUNCH_DATE = new Date();
LAUNCH_DATE.setDate(LAUNCH_DATE.getDate() + 30);

const useCountdown = (target: Date) => {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return t;
};

const ComingSoon = () => {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNotify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { error } = await supabase.from("leads").insert({
        email,
        source: "coming_soon",
        lead_type: "newsletter",
      } as any);
      if (error) throw error;
      toast.success("You're on the list! We'll be in touch soon.");
      setEmail("");
    } catch (err: any) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const Box = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center justify-center rounded-xl border border-primary/20 bg-card/60 backdrop-blur px-4 py-3 sm:px-6 sm:py-4 min-w-[70px] sm:min-w-[90px]">
      <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary tabular-nums">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground mt-1">
        {label}
      </span>
    </div>
  );

  return (
    <>
      <SEO
        title="Coming Soon | Atralend"
        description="Atralend is launching soon. Canada's premier B2B and private lending marketplace. Sign up to be notified."
        canonical="https://atralend.com/"
      />
      <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background px-4 py-12">
        {/* Background accents */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        </div>

        <main className="relative z-10 w-full max-w-3xl text-center">
          <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-xs font-medium uppercase tracking-widest text-primary mb-6">
            Coming Soon
          </span>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4">
            Something <span className="text-primary">Big</span> Is On The Way
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-10">
            We're putting the finishing touches on a brand new experience for borrowers and capital partners. Check back soon.
          </p>

          <div className="flex justify-center gap-2 sm:gap-4 mb-10">
            <Box value={days} label="Days" />
            <Box value={hours} label="Hours" />
            <Box value={minutes} label="Minutes" />
            <Box value={seconds} label="Seconds" />
          </div>

          <form
            onSubmit={handleNotify}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-8"
          >
            <Input
              type="email"
              required
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Notify Me"}
            </Button>
          </form>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-muted-foreground">
            <a
              href="mailto:info@atralend.com"
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              <Mail className="h-4 w-4" />
              info@atralend.com
            </a>
            <a
              href="tel:+1"
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              <Phone className="h-4 w-4" />
              Contact Us
            </a>
          </div>
        </main>

        <footer className="relative z-10 mt-16 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Atralend. All rights reserved.
        </footer>
      </div>
    </>
  );
};

export default ComingSoon;
